import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useBlocker } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Archive, Database, Workflow, Layers3, LayoutDashboard, Receipt } from "lucide-react";
import type { ViewKind } from "./workspaceModel";
import "./Applications.css";

export function useApplicationStyle(): CSSProperties {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return {
    "--app-bg": dark ? "#151719" : "#f7f8fa",
    "--app-panel": dark ? "#1d2023" : "#ffffff",
    "--app-text": dark ? "#f1f3f5" : "#20272f",
    "--app-muted": dark ? "#a3acb7" : "#697583",
    "--app-line": dark ? "#33383e" : "#e2e6eb",
    "--app-soft": dark ? "#242b32" : "#eef4f9",
    "--app-accent": dark ? "#6cc5ff" : "#0877b5",
    "--app-button-text": dark ? "#102534" : "#ffffff",
    "--app-good": dark ? "#86d7b0" : "#21754e",
  } as CSSProperties;
}
export function ApplicationSurface({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const style = useApplicationStyle();
  return (
    <div className={`application-surface ${className}`} style={style}>
      {children}
    </div>
  );
}
export function ViewIcon({
  kind,
  size = 18,
}: {
  kind?: ViewKind;
  size?: number;
}) {
  const Icon =
    kind === "accounts-payable"
      ? Receipt
      : kind === "overview"
      ? LayoutDashboard
      : kind === "documents"
      ? Archive
      : kind === "data"
        ? Database
        : kind === "workflow"
          ? Workflow
          : Layers3;
  return <Icon size={size} aria-hidden />;
}
export function LoadingState() {
  return (
    <div className="app-empty" role="status">
      <CircularProgress size={24} />
      <p>Loading…</p>
    </div>
  );
}
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="app-empty">
      <span className="app-mark">
        <Layers3 size={24} />
      </span>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </div>
  );
}
export function UnsavedChanges({
  dirty,
  saving,
}: {
  dirty: boolean;
  saving: boolean;
}) {
  const savingRef = useRef(saving);
  savingRef.current = saving;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      !savingRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (dirty && !savingRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  return (
    <Dialog
      disablePortal
      open={blocker.state === "blocked"}
      onClose={() => blocker.state === "blocked" && blocker.reset()}
    >
      <DialogTitle>Leave without saving?</DialogTitle>
      <DialogContent>Your changes haven’t been saved.</DialogContent>
      <DialogActions>
        <Button onClick={() => blocker.state === "blocked" && blocker.reset()}>
          Keep editing
        </Button>
        <Button
          color="error"
          onClick={() => blocker.state === "blocked" && blocker.proceed()}
        >
          Discard changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export function LoadError({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  return (
    <Alert
      severity="error"
      action={
        retry ? (
          <Button color="inherit" onClick={retry}>
            Retry
          </Button>
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
}
